import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, MessageCircle, Plus, Edit3, ArrowRight, UserPlus, UserMinus, Zap, Target, X } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Activity } from '../types';
import { getActivitiesForBoard } from '../firebase/firestore';

interface ActivityFeedProps {
  boardId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ boardId, isOpen, onClose }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && boardId) {
      loadActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadActivities is stable, avoid re-running on every ref
  }, [isOpen, boardId]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const loadActivities = async () => {
    setIsLoading(true);
    try {
      const activitiesData = await getActivitiesForBoard(boardId);
      setActivities(activitiesData);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'product_created':
        return <Plus size={16} className="text-green-500" />;
      case 'product_updated':
        return <Edit3 size={16} className="text-blue-500" />;
      case 'product_moved':
        return <ArrowRight size={16} className="text-purple-500" />;
      case 'comment_added':
        return <MessageCircle size={16} className="text-orange-500" />;
      case 'priority_changed':
        return <Zap size={16} className="text-yellow-500" />;
      case 'status_changed':
        return <Target size={16} className="text-indigo-500" />;
      case 'member_joined':
        return <UserPlus size={16} className="text-green-500" />;
      case 'member_left':
        return <UserMinus size={16} className="text-red-500" />;
      default:
        return <TrendingUp size={16} className="text-gray-500" />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'product_created':
        return 'bg-green-50 border-green-200';
      case 'product_updated':
        return 'bg-blue-50 border-blue-200';
      case 'product_moved':
        return 'bg-purple-50 border-purple-200';
      case 'comment_added':
        return 'bg-orange-50 border-orange-200';
      case 'priority_changed':
        return 'bg-yellow-50 border-yellow-200';
      case 'status_changed':
        return 'bg-indigo-50 border-indigo-200';
      case 'member_joined':
        return 'bg-green-50 border-green-200';
      case 'member_left':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 604800)}w ago`;
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const groupActivitiesByDate = (activities: Activity[]) => {
    const groups: { [key: string]: Activity[] } = {};
    
    activities.forEach(activity => {
      const dateKey = formatDate(activity.createdAt);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    
    return groups;
  };

  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <TrendingUp size={24} className="text-satsuma-500" />
                <h2 className="text-2xl font-bold text-gray-800">Activity Feed</h2>
                <span className="bg-satsuma-100 text-satsuma-700 text-sm px-2 py-1 rounded-full">
                  {activities.length}
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Activities List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-satsuma-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading activities...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp size={48} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No activity yet</p>
                  <p className="text-sm text-gray-400">Start creating products to see activity here!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedActivities).map(([date, dateActivities]) => (
                    <div key={date}>
                      <h3 className="text-sm font-medium text-gray-500 mb-3 sticky top-0 bg-white/15 backdrop-blur-sm py-2">
                        {date}
                      </h3>
                      <div className="space-y-3">
                        {dateActivities.map((activity) => (
                          <motion.div
                            key={activity.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className={`border rounded-xl p-4 ${getActivityColor(activity.type)}`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                {getActivityIcon(activity.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800 mb-1">
                                  <span className="font-medium">{activity.createdByEmail}</span>
                                  {' '}
                                  {activity.description}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatTimeAgo(activity.createdAt)}
                                </p>
                                {activity.metadata && (
                                  <div className="mt-2 text-xs text-gray-600">
                                    {activity.metadata.oldValue && activity.metadata.newValue && (
                                      <span>
                                        Changed from <span className="font-medium">{activity.metadata.oldValue}</span> to{' '}
                                        <span className="font-medium">{activity.metadata.newValue}</span>
                                      </span>
                                    )}
                                    {activity.metadata.priorityScore && (
                                      <span>
                                        Priority score: <span className="font-medium">{activity.metadata.priorityScore}</span>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActivityFeed;
